import { useState } from 'react';

import { sanitizeUrl } from '@/lib/stashstr';
import { cn } from '@/lib/utils';

/**
 * Image from an unproven (PoW-missing) post: rendered blurred with an
 * overlay until the user clicks to reveal it in place. Each image has its
 * own reveal state. Not a link until revealed (no navigation on a blurred
 * image's URL).
 *
 * Variants: 'gallery' (default) shows the full sentence; 'thumb' (64px
 * feed rows) shows a short single-word label that fits the small box.
 */
export function UnprovenImage({
  url,
  className,
  alt = '',
  variant = 'gallery',
}: {
  url: string;
  className?: string;
  alt?: string;
  variant?: 'gallery' | 'thumb';
}) {
  const [revealed, setRevealed] = useState(false);
  const safe = sanitizeUrl(url);
  if (!safe) return null;

  if (revealed) {
    return <img src={safe} alt={alt} loading="lazy" className={className} />;
  }

  const label = variant === 'thumb' ? 'unproven' : 'Unproven — click to reveal';

  return (
    <button
      type="button"
      onClick={() => setRevealed(true)}
      aria-label="Reveal blurred image from unproven post"
      title="Unproven post — click to reveal image"
      className={cn(
        'relative block overflow-hidden rounded p-0 border-0 bg-transparent cursor-pointer',
        className,
      )}
    >
      <img
        src={safe}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="w-full h-full object-cover blur-md select-none pointer-events-none"
      />
      <span
        className={cn(
          'absolute inset-0 flex items-center justify-center font-medium text-white bg-black/40 px-1 text-center',
          variant === 'thumb' ? 'text-[9px] leading-tight' : 'text-[11px]',
        )}
      >
        {label}
      </span>
    </button>
  );
}
